import React, { useMemo, useReducer } from 'react';

import { OnboardingContext, OnboardingActionContext } from './OnboardingContext';
import {
  CurrentDeviceOnboardingType,
  CurrentPointOnboardingType,
  CurrentStreamOnboardingType,
  CurrentVideoStreamOnboardingType,
  OnboardingActionContextType,
  OnboardingActionTypes,
  PlayerTypesInFlow,
  SelectPlayerContentViewMode,
  SelectPlayerFlowType,
} from './types';
import { DEFAULT_ONBOARDING_STATE } from './consts';
import { onboardingReducer } from './reducer';

const OnboardingProvider: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(onboardingReducer, DEFAULT_ONBOARDING_STATE);

  const setInFlow = (inFlow: boolean) => {
    dispatch({
      type: OnboardingActionTypes.SET_IN_FLOW,
      inFlow,
    });
  };

  const setFlowType = (flowType: SelectPlayerFlowType) => {
    dispatch({
      type: OnboardingActionTypes.SET_FLOW_TYPE,
      flowType,
    });
  };

  const setIsTemplateMode = (isTemplateMode: boolean) => {
    dispatch({
      type: OnboardingActionTypes.SET_IS_TEMPLATE_FLOW,
      isTemplateMode,
    });
  };

  const setContentViewMode = (contentViewMode: SelectPlayerContentViewMode) => {
    dispatch({
      type: OnboardingActionTypes.SET_CONTENT_VIEW_MODE,
      contentViewMode,
    });
  };

  const setSelectedPlayerInFlow = (selectedPlayerInFlow: PlayerTypesInFlow) => {
    dispatch({
      type: OnboardingActionTypes.SET_SELECTED_PLAYER_IN_FLOW,
      selectedPlayerInFlow,
    });
  };

  const setCurrentPoint = (currentPoint: CurrentPointOnboardingType) => {
    dispatch({
      type: OnboardingActionTypes.SET_CURRENT_POINT,
      currentPoint,
    });
  };

  const setCurrentPointStream = (stream: CurrentStreamOnboardingType) => {
    dispatch({
      type: OnboardingActionTypes.SET_CURRENT_POINT_STREAM,
      stream,
    });
  };

  const setCurrentPointVideoStream = (videoStream: CurrentVideoStreamOnboardingType) => {
    dispatch({
      type: OnboardingActionTypes.SET_CURRENT_POINT_VIDEO_STREAM,
      videoStream,
    });
  };

  const setCurrentPointDevice = (device: CurrentDeviceOnboardingType) => {
    dispatch({
      type: OnboardingActionTypes.SET_CURRENT_POINT_DEVICE,
      device,
    });
  };

  const setCurrentPointIsEnabled = (isEnabled: boolean) => {
    dispatch({
      type: OnboardingActionTypes.SET_CURRENT_POINT_IS_ENABLED,
      isEnabled,
    });
  };

  const clearCurrentPoint = () => {
    dispatch({
      type: OnboardingActionTypes.RESET_CURRENT_POINT,
    });
  };

  const resetState = () => {
    dispatch({
      type: OnboardingActionTypes.RESET_STATE,
    });
  };

  const actionContextValue: OnboardingActionContextType = useMemo(
    () => ({
      setInFlow,
      setFlowType,
      setIsTemplateMode,
      setContentViewMode,
      setSelectedPlayerInFlow,
      setCurrentPoint,
      setCurrentPointStream,
      setCurrentPointVideoStream,
      setCurrentPointDevice,
      setCurrentPointIsEnabled,
      clearCurrentPoint,
      resetState,
    }),
    [
      setInFlow,
      setFlowType,
      setIsTemplateMode,
      setContentViewMode,
      setCurrentPoint,
      setCurrentPointStream,
      setCurrentPointVideoStream,
      setCurrentPointDevice,
      setCurrentPointIsEnabled,
      clearCurrentPoint,
      resetState,
    ]
  );

  return (
    <OnboardingContext.Provider value={state}>
      <OnboardingActionContext.Provider value={actionContextValue}>{children}</OnboardingActionContext.Provider>
    </OnboardingContext.Provider>
  );
};

export default OnboardingProvider;
